import React, { useMemo } from 'react';
import Box from '@mui/material/Box';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { createFilterOptions } from '@mui/material/Autocomplete';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import FieldLabel from '../components/FieldLabel';
import { getFieldLabel, parsePhoneValue, blockInvalidNumberKeys } from '../utils/formHelpers';

const PhoneField = ({
    field,
    data,
    change,
    hasError,
    errorMessage,
    countryCodes = [],
    getByDialCode,
}) => {
    const { dialCode, number } = parsePhoneValue(data[field.key], countryCodes);
    const selectedCode = getByDialCode?.(dialCode) || null;

    const filterCountryCodeOptions = useMemo(
        () => createFilterOptions({
            stringify: (option) => `${option.dialCode} ${option.country}`,
        }),
        []
    );

    const commitPhone = (nextDial, nextNumber) => {
        const digits = String(nextNumber || '').replace(/[^\d\s\-()]/g, '');
        if (!nextDial && !digits) {
            change(field.key, '');
            return;
        }
        change(field.key, `${nextDial || ''}${digits ? ` ${digits}` : ''}`.trim());
    };

    return (
        <Box>
            <MDTypography variant="caption" color="text" display="block" mb={0.5}>
                <FieldLabel label={getFieldLabel(field)} tooltip={field.tooltip} />
            </MDTypography>
            <MDBox display="flex" gap={1}>
                <Autocomplete
                    size="small"
                    options={countryCodes}
                    filterOptions={filterCountryCodeOptions}
                    getOptionLabel={(option) => option.dialCode}
                    value={selectedCode}
                    onChange={(_, value) => commitPhone(value?.dialCode || '', number)}
                    isOptionEqualToValue={(option, value) => option.dialCode === value.dialCode}
                    renderOption={(props, option) => (
                        <MDBox
                            component="li"
                            {...props}
                            key={option.iso2}
                            display="flex"
                            gap={1}
                            sx={{ whiteSpace: 'nowrap' }}
                        >
                            <MDTypography variant="caption" color="text">{option.dialCode}</MDTypography>
                            <MDTypography variant="caption" color="text">{option.country}</MDTypography>
                        </MDBox>
                    )}
                    slotProps={{
                        popper: { sx: { width: '260px !important' } },
                    }}
                    sx={{
                        flex: '0 0 30%',
                        minWidth: 0,
                        '& .MuiAutocomplete-input': { minWidth: '30px !important' },
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            placeholder="+91"
                            error={hasError}
                        />
                    )}
                />
                <TextField
                    size="small"
                    fullWidth
                    placeholder="98765 43210"
                    value={number}
                    error={hasError}
                    onChange={(e) => commitPhone(dialCode, e.target.value)}
                    onKeyDown={blockInvalidNumberKeys}
                />
            </MDBox>
            {hasError && (
                <MDTypography variant="caption" color="error" display="block" mt={0.5}>
                    {errorMessage || 'This field is required'}
                </MDTypography>
            )}
        </Box>
    );
};

export default PhoneField;
